import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'react-hot-toast'

import {
  BaseView,
  BaseSafeFormView,
  HeaderNavigation,
  AppButton,
  BaseText,
} from '@/components'
import { TextInputForm } from '@/components/form'
import { useWithdrawStore } from '@/store/useWithdrawStore'
import {
  useCreateWithdrawCryptoAddress,
  useFetchWithdrawCryptoAddressByNetworkCode,
} from '@/domain/wallet'
import { useCheckExternalWalletAddress } from '@/domain/transfer'
import { useStartMFA } from '@/domain/mfa'
import { CreateCryptoAddressWithdrawPayload } from '@/domain/wallet/walletTypes'
import { MFACodeValidatorScreenParams } from '@/app/mfa-code-validator'
import {
  useCreateNewCryptoWalletAddressSchema,
  ICreateNewCryptoWalletAddressSchema,
} from '@/schemas/useCreateNewCryptoWalletAddressSchema'
import useOffsets from '@/hooks/useOffsets'
import { Scan } from '@/assets/icons'

export const WithdrawRegisterWalletAddressScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const offsets = useOffsets()
  const hasProcessedMfaToken = useRef(false)

  const blockchain = useWithdrawStore((state) => state.blockchain)
  const walletAddressRegisterForm = useWithdrawStore(
    (state) => state.walletAddressRegisterForm,
  )
  const resetWalletAddressForm = useWithdrawStore(
    (state) => state.resetWalletAddressForm,
  )
  const setExternalWalletAddress = useWithdrawStore(
    (state) => state.setExternalWalletAddress,
  )
  const mfaPayload = useWithdrawStore((state) => state.mfaPayload)
  const setMfaPayload = useWithdrawStore((state) => state.setMfaPayload)
  const resetMfaPayload = useWithdrawStore((state) => state.resetMfaPayload)

  const schema = useCreateNewCryptoWalletAddressSchema()

  // Get list of saved addresses to check for duplicates
  const { data: savedAddresses } = useFetchWithdrawCryptoAddressByNetworkCode({
    networkCode: blockchain?.code || '',
  })

  const {
    mutateAsync: createWithdrawCryptoAddress,
    isPending: isLoadingCreateWithdrawCryptoAddress,
  } = useCreateWithdrawCryptoAddress({
    onSuccess: (payload) => {
      toast.success(t('translation:addressRegistered'))

      resetWalletAddressForm()
      resetMfaPayload()
      setExternalWalletAddress({
        address: payload.address,
        name: payload.name,
      })

      setTimeout(() => {
        navigate('/withdraw/review-and-confirm')
      }, 200)
    },
    onError: ({ errorMessage }) => {
      toast.error(errorMessage ?? 'Error not mapped')
    },
  })

  const {
    mutateAsync: checkExternalWalletAddress,
    isPending: isLoadingCheckExternalWalletAddress,
  } = useCheckExternalWalletAddress()

  const { mutateAsync: startMFA, isPending: isLoadingStartMFA } = useStartMFA({
    onSuccess: ({ challenge_id }, payload) => {
      if (!payload?.request_payload) {
        return toast.error(t('api:errors.somethingWrong'))
      }

      const mfaCodeValidatorParams: MFACodeValidatorScreenParams = {
        backRoute: '/withdraw/register-wallet-address',
        challengeId: challenge_id,
        requestPayload: payload.request_payload,
      }

      navigate('/mfa-code-validator', {
        state: mfaCodeValidatorParams,
      })
    },
    onError: ({ errorMessage }) => {
      toast.error(errorMessage ?? t('api:errors.somethingWrong'))
    },
  })

  const isLoadingSave =
    isLoadingCreateWithdrawCryptoAddress ||
    isLoadingCheckExternalWalletAddress ||
    isLoadingStartMFA

  const { control, handleSubmit, formState, setValue, watch } =
    useForm<ICreateNewCryptoWalletAddressSchema>({
      resolver: zodResolver(schema),
      defaultValues: {
        addressName: '',
        address: '',
      },
      mode: 'onChange',
    })

  // Restore form data when screen gets focus
  useEffect(() => {
    if (walletAddressRegisterForm?.address) {
      setValue('address', walletAddressRegisterForm.address, {
        shouldValidate: walletAddressRegisterForm.address.length >= 26,
      })
    }
  }, [walletAddressRegisterForm, setValue])

  // Handle return from MFA screen with token
  useEffect(() => {
    const verifyMfaToken = async () => {
      const locationState = location.state as { token?: string } | undefined
      const mfaToken = locationState?.token

      if (
        mfaToken &&
        mfaPayload &&
        mfaPayload.address &&
        mfaPayload.name &&
        mfaPayload.network_code &&
        !hasProcessedMfaToken.current
      ) {
        hasProcessedMfaToken.current = true
        await createWithdrawCryptoAddress({
          grantToken: mfaToken,
          network_code: mfaPayload.network_code,
          address: mfaPayload.address,
          name: mfaPayload.name,
        })
      }
    }

    verifyMfaToken()
  }, [location.state, mfaPayload, createWithdrawCryptoAddress])

  const validateAddressAndName = (
    formData: ICreateNewCryptoWalletAddressSchema,
  ): boolean => {
    const addresses = savedAddresses?.crypto_addresses || []
    const trimmedAddress = formData.address.trim()
    const trimmedName = (formData.addressName || '').trim()

    // Check for existing address
    const addressExists = addresses.some(
      (saved) => saved.address.toLowerCase() === trimmedAddress.toLowerCase(),
    )

    if (addressExists) {
      toast.error(t('api:errors.addressAlreadyExists'))
      return false
    }

    // Check for existing name (if name was entered)
    if (trimmedName) {
      const nameExists = addresses.some(
        (saved) => saved.name.toLowerCase() === trimmedName.toLowerCase(),
      )

      if (nameExists) {
        toast.error(t('api:errors.addressNameAlreadyExists'))
        return false
      }
    }

    return true
  }

  const handleCheckExternalWalletAddress = async (
    formData: ICreateNewCryptoWalletAddressSchema,
  ) => {
    if (!blockchain?.code) {
      toast.error(t('api:errors.somethingWrong'))
      return false
    }

    try {
      await checkExternalWalletAddress({
        destination_address: formData.address,
        blockchain_network: blockchain.code,
      })

      return true
    } catch {
      toast.error(t('api:validationErrors.pleaseUseAnotherAddress'))
      return false
    }
  }

  const handleWithdraw = async (
    formData: ICreateNewCryptoWalletAddressSchema,
  ) => {
    if (!blockchain?.code) {
      toast.error(t('api:errors.somethingWrong'))
      return
    }

    const requestPayload: CreateCryptoAddressWithdrawPayload = {
      network_code: blockchain.code,
      address: formData.address,
      name: formData.addressName || t('common:myWallet'),
    }

    setMfaPayload(requestPayload)

    await startMFA({
      action: 'withdrawal_address_add',
      mfa_method: 'email',
      request_payload: requestPayload as unknown as Record<string, unknown>,
    })
  }

  const handleSubmitForm = async (
    formData: ICreateNewCryptoWalletAddressSchema,
  ) => {
    // First check for local duplicates
    const isUniqueAddressAndName = validateAddressAndName(formData)
    if (!isUniqueAddressAndName) {
      return
    }

    // Then validate address via API
    const isValidExternalWalletAddress =
      await handleCheckExternalWalletAddress(formData)

    if (isValidExternalWalletAddress) {
      await handleWithdraw(formData)
    }
  }

  // Get current form values
  const addressValue = watch('address')

  // Simple check - is there an address and is it long enough
  const hasValidAddress = addressValue && addressValue.trim().length >= 20
  const isValid = (formState.isValid || hasValidAddress) && !isLoadingSave

  // Handler for Scan QR button
  const handleScanQRPress = () => {
    // TODO: Implement navigation to QR code scanning screen
    // In RN version this is navigate('/withdraw/scan-wallet-address')
    navigate('/withdraw/scan-wallet-address')
  }

  return (
    <BaseSafeFormView style={{ paddingTop: offsets.top }}>
      <HeaderNavigation
        rightIcon={
          <button
            onClick={handleScanQRPress}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
              padding: '4px',
              paddingRight: '15px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <Scan size={20} color="#7572FF" />
            <BaseText
              fontSize={14}
              fontFamily="semibold"
              color="label.default"
              style={{ whiteSpace: 'nowrap' }}
            >
              {t('form:actions.scanQR')}
            </BaseText>
          </button>
        }
      />

      <BaseView px={13} flex={1} pt={24} gap={24}>
        <BaseView gap={16}>
          <BaseText fontSize={22} className="text-lg font-semibold text-white">
            {t('screens:withdraw.enterWalletAddress')}
          </BaseText>
          {/* <p className="text-sm text-gray-400">
            {t('screens:withdraw.enterWalletAddressDescription')}
          </p> */}
        </BaseView>

        <BaseView gap={16}>
          {/* Address name (optional) */}
          <TextInputForm
            control={control}
            name="addressName"
            label={`${t('translation:addressName')} (${t('translation:optional')})`}
            placeholder={t('translation:enterAddressName')}
            maxLength={50}
          />

          {/* Wallet address */}
          <TextInputForm
            control={control}
            name="address"
            label={`${t('translation:walletAddress')} *`}
            placeholder={t('form:placeholders.enterWalletAddress')}
            isTextArea={true}
          />
        </BaseView>
      </BaseView>

      {/* Save button */}
      <BaseView px={13} style={{ paddingBottom: offsets.bottom }}>
        <AppButton
          label={isLoadingSave ? t('common:loading') : t('form:actions.save')}
          onPress={handleSubmit(handleSubmitForm)}
          disabled={!isValid}
          isLoading={isLoadingSave}
          type="primaryGradient"
          style={{ width: '100%' }}
        />
      </BaseView>
    </BaseSafeFormView>
  )
}
